import { Injectable } from '@angular/core';
import { Effect, Actions, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { Action, select, Store } from '@ngrx/store';
import * as fromRouter from '~core/store';
import { switchMap, map, catchError, filter, withLatestFrom, takeUntil } from 'rxjs/operators';

import {
  LoadStoryblokSeoContent,
  LoadStoryblokSeoContentFailure,
  LoadStoryblokSeoContentSuccess,
  StoryblokSeoActionTypes,
} from './storyblok-seo.actions';
import { StoryblokSeoInitialState } from './storyblok-seo.state';
import { ErrorResponse } from '~shared/models';
import { UrlUtils } from '~app/modules/generic/utils/url.utils';
import { getCategory } from '~app/modules/generic/store';
import { StoryblokService } from '~app/modules/storyblok/services';
import { getCountryDomainCode } from '~server/utils/country-detection.util';
import { CountryCode } from '~shared/constants';
import { SB_CATEGORY_PLP_PAGE_ROUTE } from '~app/modules/catalog/constants';
import { StoryblokApiResponse } from '~app/modules/storyblok/models/storyblok-api-response.model';

@Injectable({
  providedIn: 'root',
})
export class StoryblokSeoEffects {
  private activeRoute$ = this.store$.pipe(select(fromRouter.getRouterState));

  constructor(
    private actions$: Actions,
    private storyblokService: StoryblokService,
    private store$: Store<fromRouter.RouterState>
  ) {}

  @Effect()
  loadSeoContent$: Observable<Action> = this.actions$.pipe(
    ofType(StoryblokSeoActionTypes.LOAD_STORYBLOK_SEO_CONTENT),

    switchMap((action: LoadStoryblokSeoContent) => {
      if (action.payload.categorySlug) {
        const market: CountryCode = getCountryDomainCode();
        const url: string = `${SB_CATEGORY_PLP_PAGE_ROUTE}/${action.payload.categorySlug}`.replace('{market}', market);

        return this.storyblokService.getStoryByHttp(url).pipe(
          map(
            (storyblokSEOContent: StoryblokApiResponse) => new LoadStoryblokSeoContentSuccess({ storyblokSEOContent })
          ),
          catchError((error: ErrorResponse) => of(new LoadStoryblokSeoContentFailure({ error })))
        );
      } else {
        return of(
          new LoadStoryblokSeoContentSuccess({ storyblokSEOContent: StoryblokSeoInitialState.storyblokSEOContent })
        );
      }
    })
  );

  @Effect()
  LoadSeoContentByCategory$: Observable<Action> = this.store$.pipe(select(getCategory)).pipe(
    withLatestFrom(this.activeRoute$),
    filter(([category, route]) => !!category && route && UrlUtils.isCategoryPage(route.url)),
    map(([category, _]) => ({ categorySlug: category.slug })),
    switchMap((payload) => of(new LoadStoryblokSeoContent(payload)))
  );
}
